import { useLayoutEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Outlet } from 'react-router';
import { createGlobalStyle } from 'styled-components';

import { TwitchPlayer } from '@sorare/core/src/components/TwitchPlayer';
import { SidetabHandler as TypeformSidetabHandler } from '@sorare/core/src/components/typeform/SidetabHandler';
import { StyleSheetManager } from '@sorare/core/src/contexts/StyleSheetManager';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { Maintenance } from '@sorare/core/src/routing/Maintenance';
import { MyBidsPictureInPictureProvider } from '@sorare/marketplace/src/contexts/myBidsPictureInPicture/Provider';

import { AppProviders } from './AppProviders.tsx';
import { LazyBlockchainProviders } from './components/BlockChainProviders/lazy.tsx';
import { MobileAppInit } from './components/MobileAppInit/index.tsx';

export const InnerAppLayout = () => {
  const { currentUser } = useCurrentUserContext();

  if (currentUser) {
    return (
      <LazyBlockchainProviders>
        <TypeformSidetabHandler />
        <MyBidsPictureInPictureProvider>
          <Outlet />
        </MyBidsPictureInPictureProvider>
      </LazyBlockchainProviders>
    );
  }
  return <Outlet />;
};

const GlobalBackground = createGlobalStyle`
  body {
    color: var(--c-onBase-primary);
    background: var(--c-surface-background);
  }
  `;

const LightMode = () => {
  const { lifecycle } = useLifecycle();
  const lightMode = lifecycle?.[LIFECYCLE.experimentalLightMode];
  useLayoutEffect(() => {
    if (lightMode) {
      document.documentElement.classList.add('light-mode');
    }
    return () => document.documentElement.classList.remove('light-mode');
  }, [lightMode]);

  return null;
};

export const OuterAppLayout = () => {
  const {
    flags: { useMaintenance },
  } = useFeatureFlags();

  if (useMaintenance?.maintenance) {
    return (
      <Maintenance
        startDate={new Date(useMaintenance.start_date)}
        endDate={new Date(useMaintenance.end_date)}
      />
    );
  }

  return (
    <HelmetProvider>
      <GlobalBackground />
      <MobileAppInit />
      <StyleSheetManager>
        <AppProviders>
          <LightMode />
          <Outlet />
          <TwitchPlayer />
        </AppProviders>
      </StyleSheetManager>
    </HelmetProvider>
  );
};
